import { shallowEqual } from 'react-redux'
import { createSelectorCreator } from 'reselect'

/**
 * Create memoized function that maintains infinite entries by entity ID (last parameter)
 * and state (1 ... N-1 parameters).
 *
 * When state changes, the entity cache is cleared.
 *
 * This is very specific memoize designed for selectors which _last_ argument is some entity ID.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function memoizeByEntityId<FuncT extends (...args: any[]) => any>(
  func: FuncT,
  options: { equalityCheck?: (a: unknown, b: unknown) => boolean } = {}
): FuncT {
  const entries = new Map<unknown, ReturnType<FuncT>>()
  const entriesByEntityId = new Map<unknown, ReturnType<FuncT>>()
  let lastState: unknown = undefined
  return ((...args: unknown[]) => {
    if (args.length < 2) {
      throw new Error('memoizeByEntityId requires at least 2 arguments')
    }
    const entityId = args.pop()
    if (!shallowEqual(lastState, args)) {
      entries.clear()
      lastState = args
    }

    if (entries.has(entityId)) {
      return entries.get(entityId) as ReturnType<FuncT>
    }
    const equalityCheck = options.equalityCheck

    const result = func(...args, entityId)
    if (equalityCheck) {
      if (entriesByEntityId.has(entityId)) {
        const previousResult = entriesByEntityId.get(entityId)
        if (equalityCheck(result, previousResult)) {
          return previousResult
        }
      }
      entriesByEntityId.set(entityId, result as ReturnType<FuncT>)
    }
    entries.set(entityId, result as ReturnType<FuncT>)
    return result as ReturnType<FuncT>
  }) as FuncT
}

/**
 * Create selector that memoizes by entity ID (last argument) and state (initial arguments).
 *
 * See problem description: https://reselect.js.org/api/weakMapMemoize.
 *
 * Example usage:
 * ```
 *    const fooBarSelector = createEntitySelector(
 *        (state) => state.foo, // if changes, clear cache
 *        (state) => state.bar, // if changes, clear cache
 *        (_, entityId) => entityId, // cache by this _within_ same state of above selectors
 *        (foo, bar, entityId) => { ... foo[entityId], ...bar[entityId] }
 *    )
 *
 *    fooBarSelector(state, 1) // calls selector(state, 1)
 *    fooBarSelector(state, 1) // returns cached value
 *    fooBarSelector(state, 2) // calls selector(state, 2)
 *    fooBarSelector(state, 1) // returns cached value
 *    // only state change will clear cache
 * ```
 *
 */
export const createEntitySelector = createSelectorCreator(memoizeByEntityId)

export const createMemoizeById = <EntityT extends { id: string }>(options?: {
  equalityCheck?: (a: EntityT, b: EntityT) => boolean
}) => {
  const entries = new Map<unknown, EntityT>()
  const equalityCheck = options?.equalityCheck || shallowEqual

  return (entity: EntityT): EntityT => {
    const entityId = entity.id
    if (entries.has(entityId)) {
      const oldEntity = entries.get(entityId) as EntityT
      if (equalityCheck(oldEntity, entity)) {
        return oldEntity
      }
    }
    entries.set(entityId, entity)
    return entity
  }
}
